// === Admin Portal Settings Dialog ===
// Theme colour picker that mirrors the ICUDMIN settings panel.

import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  Button,
  Snackbar,
  Alert,
  Switch,
  FormControlLabel,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useThemeContext, themeOptions } from '../lib/theme-context';

interface SettingsDialogProps {
  readonly open: boolean;
  readonly onClose: () => void;
}

export function SettingsDialog({ open, onClose }: SettingsDialogProps) {
  const { currentTheme, changeTheme, gradientCards, toggleGradientCards } = useThemeContext();
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const handleThemeChange = async (themeId: string): Promise<void> => {
    await changeTheme(themeId);
  };

  const handleSave = (): void => {
    setSnackbarOpen(true);
  };

  const handleReset = async (): Promise<void> => {
    await changeTheme('default');
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              backgroundColor: '#191c24',
              backgroundImage: 'none',
              borderRadius: 2,
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            color: '#ffffff',
            pb: 1,
          }}
        >
          Settings
          <IconButton onClick={onClose} size="small" sx={{ color: '#6c7293' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 0 }}>
          {/* Theme Color Card */}
          <Box sx={{ backgroundColor: '#191c24', borderRadius: 2, p: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ color: '#ffffff' }}>
              Theme Color
            </Typography>
            <Typography variant="body2" sx={{ color: '#6c7293', mb: 3 }}>
              Choose a color theme for your dashboard
            </Typography>

            {/* Colour swatches grid */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))',
                gap: 2,
                mb: 3,
              }}
            >
              {themeOptions.map((theme) => (
                <Box
                  key={theme.id}
                  onClick={() => { void handleThemeChange(theme.id); }}
                  sx={{
                    cursor: 'pointer',
                    textAlign: 'center',
                    p: 1,
                    borderRadius: 2,
                    border: currentTheme === theme.id
                      ? '3px solid #00FFFF'
                      : '1px solid #3a4553',
                    transition: 'all 0.2s',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 8px rgba(0, 255, 255, 0.2)',
                    },
                  }}
                >
                  {/* Swatch preview — top half bg colour, bottom half paper colour */}
                  <Box sx={{ width: '100%', height: 52, borderRadius: 1, mb: 1, background: theme.gradient, border: theme.id === 'white' ? '1px solid #ddd' : 'none' }} />
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#ffffff',
                      fontWeight: currentTheme === theme.id ? 'bold' : 'normal',
                    }}
                  >
                    {theme.name}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Display Options Card */}
          <Box sx={{ backgroundColor: '#191c24', borderRadius: 2, p: 2, mt: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ color: '#ffffff' }}>
              Display Options
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={gradientCards}
                  onChange={toggleGradientCards}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': { color: '#00FFFF' },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#00FFFF' },
                  }}
                />
              }
              label={
                <Box>
                  <Typography variant="body2" sx={{ color: '#ffffff', fontWeight: 600 }}>Card Gradients</Typography>
                  <Typography variant="caption" sx={{ color: '#6c7293' }}>Show colour gradient tints on dashboard nav cards</Typography>
                </Box>
              }
            />
          </Box>

          {/* Action buttons */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
              <Button
                variant="outlined"
                onClick={() => { void handleReset(); }}
                sx={{
                  borderColor: '#6c7293',
                  color: '#6c7293',
                  '&:hover': { borderColor: '#ffffff', color: '#ffffff' },
                }}
              >
                Reset to Default
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                sx={{
                  backgroundColor: '#00FFFF',
                  color: '#000000',
                  '&:hover': { backgroundColor: '#00E5E5' },
                }}
              >
                Save Changes
              </Button>
            </Box>
        </DialogContent>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity="success"
          variant="filled"
          sx={{ width: '100%' }}
        >
          Theme saved successfully
        </Alert>
      </Snackbar>
    </>
  );
}
